import { useTranslation } from 'react-i18next';
import { CalendarOff } from 'lucide-react';
import { cn } from 'lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import type { AvailabilityState, UnavailabilityPeriod } from 'api/unavailabilitiesApi';
import { AvailabilityBadge, PeriodBadge, PERIOD_DOT } from 'components/AvailabilityBadge';
import { formatPeriod } from 'utils/unavailability';

interface UnavailabilitySectionProps {
  periods: UnavailabilityPeriod[];
  /** Derived, and independent of the account's status: an unavailable technician stays active. */
  availability: AvailabilityState;
}

/**
 * The periods the technician declared from his phone.
 *
 * **Read-only, without exception.** D4 is settled: the technician is the sole author, the
 * backoffice reads. No form, no delete, no "correct it" — not because nobody got round to it, but
 * because there is no write route to call. The card says so in words, so an ops does not go
 * looking for the button.
 *
 * The real case "the technician rings up to say he is ill" still has no product answer. It is to
 * be reopened with Julien if the field asks for it, not worked around here.
 */
const UnavailabilitySection = ({ periods, availability }: UnavailabilitySectionProps) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarOff className="h-4 w-4" aria-hidden="true" />
            {t('Unavailability')}
          </CardTitle>
          <AvailabilityBadge state={availability} />
        </div>
        <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400">
          {t('Periods declared by the technician from his application — read-only, and independent of the account status.')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {periods.length === 0 ? (
          <p className="text-[12.5px] text-zinc-400 dark:text-zinc-500">
            {t('No unavailability period declared — the technician is eligible for interventions.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {periods.map((period) => (
              <li
                key={period.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-zinc-200 px-3.5 py-3 dark:border-zinc-800"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <span
                    className={cn('h-2 w-2 shrink-0 rounded-full', PERIOD_DOT[period.state])}
                    aria-hidden="true"
                  />
                  <span className="font-mono text-[13.5px] font-medium text-zinc-900 dark:text-zinc-50">
                    {formatPeriod(period.startDate, period.endDate)}
                  </span>
                </span>
                <span className="flex items-center gap-2.5">
                  {/* The reason as the technician typed it: it is his own words, not a code. */}
                  {period.reason && (
                    <span className="max-w-[220px] truncate text-xs text-zinc-500 dark:text-zinc-400">
                      {period.reason}
                    </span>
                  )}
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    {t('{{count}} day', { count: period.days })}
                  </span>
                  <PeriodBadge state={period.state} className="text-[11.5px]" />
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-[12.5px] leading-relaxed text-zinc-500 dark:text-zinc-400">
          {t('During an unavailability period the technician stays active and in the pool, but is not proposed for an intervention covering it. At the end of the period he becomes eligible again automatically.')}
        </p>
      </CardContent>
    </Card>
  );
};

export default UnavailabilitySection;
